import React, { useMemo } from "react";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useProjectAiaSummary } from "../../../hooks/useProjectAiaSummary";
import Table, { Column } from "Components/Table/Table";

const amountOf = (money?: { amount?: number | null }) =>
  Number(money?.amount ?? 0);

const formatMoney = (
  money?: {
    amount?: number | null;
    currency?: { code?: string } | null;
  } | null,
) => {
  if (!money || money.amount == null) return "—";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency?.code || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(money.amount ?? 0));
};

const formatPercent = (value?: number | string) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0%";
  return `${Number.isInteger(num) ? num : num.toFixed(2)}%`;
};

const getPctClass = (pct?: number) => {
  const n = Number(pct ?? 0);
  if (n === 100) return "pct-high";
  if (n === 0) return "pct-zero";
  if (n < 30) return "pct-warn";
  return "";
};

const G702_SKELETON_ROWS = Array.from({ length: 11 }, (_, index) => index);
const G703_SKELETON_ROWS = Array.from({ length: 6 }, (_, index) => index);

interface AIAContentProps {
  projectId?: string;
}

export default function AIAContent({ projectId }: AIAContentProps) {
  const { summary, loading, error } = useProjectAiaSummary(projectId, "lender");
  const g702 = summary?.g702;
  const g703Rows = summary?.g703?.lineItems ?? [];
  const retainageRate =
    summary?.g703?.retainageRate || g702?.retainageRate || "";

  const g702Lines = useMemo(() => {
    if (!g702) return [];

    return [
      {
        num: "1",
        label: "Original contract sum",
        val: formatMoney(g702.originalContractSum),
      },
      // { num: "1a", label: "SOV update", val: formatMoney(g702.sovUpdate) },
      {
        num: "2",
        label: "Net change by change orders",
        val: formatMoney(g702.netChangeByChangeOrders),
      },
      {
        num: "3",
        label: "Contract sum to date",
        val: formatMoney(g702.contractSumToDate),
      },
      {
        num: "4",
        label: "Total completed & stored to date",
        val: formatMoney(g702.totalCompletedAndStored),
      },
      {
        num: "5a",
        label: `Retainage: ${formatPercent(g702.retainageRate)} of completed work`,
        val: formatMoney(g702.completedWorkRetainage),
      },
      {
        num: "5b",
        label: "Retainage: stored material",
        val: formatMoney(g702.storedAmountRetainage),
      },
      {
        num: "5c",
        label: "Total retainage",
        val: formatMoney(g702.totalRetainage),
      },
      {
        num: "6",
        label: "Total earned less retainage",
        val: formatMoney(g702.totalEarnedLessRetainage),
        cls: "blue",
      },
      {
        num: "7",
        label: "Less previous certificates for payment",
        val: formatMoney(g702.lessPreviousCertificates),
      },
      {
        num: "8",
        label: "Current payment due",
        val: formatMoney(g702.currentPaymentDue),
        cls: "green",
        total: true,
      },
      {
        num: "9",
        label: "Balance to finish, including retainage",
        val: formatMoney(g702.balanceToFinish),
      },
    ];
  }, [g702]);

  const totals = useMemo(() => {
    const computed = g703Rows.reduce(
      (acc, row) => ({
        sched: acc.sched + amountOf(row.sovValue),
        prev: acc.prev + amountOf(row.previousBilled),
        curr: acc.curr + amountOf(row.currentApplication),
        stored: acc.stored + amountOf(row.currentStoredMaterial),
        total: acc.total + amountOf(row.totalCompletedAndStored),
        remain: acc.remain + amountOf(row.balanceToFinish),
        retainage: acc.retainage + amountOf(row.retainageAmount),
      }),
      {
        sched: 0,
        prev: 0,
        curr: 0,
        stored: 0,
        total: 0,
        remain: 0,
        retainage: 0,
      },
    );

    const currency =
      g703Rows[0]?.sovValue?.currency || g702?.contractSumToDate?.currency;
    const pct =
      computed.sched > 0 ? (computed.total / computed.sched) * 100 : 0;

    const wrapMoney = (amount: number) => ({ amount, currency });

    return {
      sched: wrapMoney(computed.sched),
      prev: wrapMoney(computed.prev),
      curr: wrapMoney(computed.curr),
      stored: wrapMoney(computed.stored),
      total: wrapMoney(computed.total),
      remain: wrapMoney(computed.remain),
      retainage: wrapMoney(computed.retainage),
      pct,
    };
  }, [g702, g703Rows]);

  return (
    <div className="card">
      <div className="card-title">
        G702 — Application &amp; Certificate for Payment
        {retainageRate ? (
          <span className="cardSubTitle">
            Retainage rate: {formatPercent(retainageRate)}
          </span>
        ) : null}
      </div>
      {loading ? (
        G702_SKELETON_ROWS.map((index) => (
          <div className="aia-line" key={`bank-g702-skeleton-${index}`}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                maxWidth: 360,
              }}
            >
              <Skeleton width={18} height={14} />
              <Skeleton
                width={index === 7 ? "58%" : index % 2 === 0 ? "70%" : "52%"}
                height={14}
              />
            </span>
            <Skeleton width={index === 9 ? 120 : 96} height={16} />
          </div>
        ))
      ) : error ? (
        <div className="badge b-review">{error}</div>
      ) : g702Lines.length ? (
        g702Lines.map((line) => (
          <div
            className={`aia-line${line.total ? " total" : ""}`}
            key={line.num}
          >
            <span>
              <span className="aia-lnum">{line.num}</span>
              <span
                className="aia-label"
                style={line.total ? { fontWeight: 600 } : {}}
              >
                {line.label}
              </span>
            </span>
            <span className={`aia-val${line.cls ? ` ${line.cls}` : ""}`}>
              {line.val}
            </span>
          </div>
        ))
      ) : (
        <div className="aia-line">
          <span className="aia-label">No G702 data available.</span>
        </div>
      )}

      <div className="card">
        <div className="card-title">
          G703 — Continuation Sheet (Schedule of Values)
        </div>
        <Table
          tableClassName="g703"
          data={
            loading
              ? G703_SKELETON_ROWS.map(() => ({}) as (typeof g703Rows)[number])
              : g703Rows
          }
          columns={
            [
              {
                header: "Item",
                className: "g703ThCenter",
                render: (row, i) =>
                  loading ? (
                    <Skeleton width={46} height={14} />
                  ) : (
                    row.aiaItem?.itemLabel || row.aiaItemCode || i + 1
                  ),
              },
              {
                header: "Description",
                render: (_, i) =>
                  loading ? (
                    <Skeleton width={i % 2 === 0 ? "72%" : "58%"} height={14} />
                  ) : (
                    g703Rows[i]?.description ||
                    g703Rows[i]?.aiaItem?.itemName ||
                    "—"
                  ),
              },
              {
                header: "Scheduled",
                render: (row) =>
                  loading ? (
                    <Skeleton width={88} height={14} />
                  ) : (
                    formatMoney(row.sovValue)
                  ),
              },
              {
                header: "Prev. App.",
                render: (row) =>
                  loading ? (
                    <Skeleton width={78} height={14} />
                  ) : (
                    formatMoney(row.previousBilled)
                  ),
              },
              {
                header: "This Period",
                render: (row) =>
                  loading ? (
                    <Skeleton width={78} height={14} />
                  ) : (
                    formatMoney(row.currentApplication)
                  ),
              },
              {
                header: "Materials",
                render: (row) =>
                  loading ? (
                    <Skeleton width={78} height={14} />
                  ) : (
                    formatMoney(row.currentStoredMaterial)
                  ),
              },
              {
                header: "Total",
                render: (row) =>
                  loading ? (
                    <Skeleton width={84} height={14} />
                  ) : (
                    formatMoney(row.totalCompletedAndStored)
                  ),
              },
              {
                header: "%",
                render: (row) =>
                  loading ? (
                    <Skeleton width={48} height={14} />
                  ) : (
                    <span className={getPctClass(row.percentageComplete)}>
                      {formatPercent(row.percentageComplete)}
                    </span>
                  ),
              },
              {
                header: "Balance",
                render: (row) =>
                  loading ? (
                    <Skeleton width={88} height={14} />
                  ) : (
                    formatMoney(row.balanceToFinish)
                  ),
              },
              {
                header: "Retainage",
                render: (row) =>
                  loading ? (
                    <Skeleton width={72} height={14} />
                  ) : (
                    formatMoney(row.retainageAmount)
                  ),
              },
            ] as Column<(typeof g703Rows)[number]>[]
          }
          footerRow={
            !loading && g703Rows.length
              ? [
                  <td key="0" colSpan={2}>
                    TOTAL
                  </td>,
                  formatMoney(totals.sched),
                  formatMoney(totals.prev),
                  formatMoney(totals.curr),
                  formatMoney(totals.stored),
                  formatMoney(totals.total),
                  <td key="6" className={getPctClass(totals.pct)}>
                    {formatPercent(totals.pct)}
                  </td>,
                  formatMoney(totals.remain),
                  formatMoney(totals.retainage),
                ]
              : undefined
          }
          footerRowClassName="total-row"
          fallbackUI={
            error ? (
              <span>{error}</span>
            ) : (
              <span>No G703 line items available.</span>
            )
          }
        />
      </div>
    </div>
  );
}
